import { DialogService } from 'primeng/dynamicdialog';
import { exclusionLinkedDocument, pupilModel } from '@exclusionmodule/models';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
  AfterContentChecked,
  inject
} from '@angular/core';
import {
  ApiServiceService,
  NotificationService,
  DownloadService
} from '@oneeducation/app-core';
import { EXCLUSION_ENDPOINT, ADMIN_ENDPOINT } from '@shared/properties';
import {
  AccessibilityService,
  DataLossService,
  DataSortService
} from '@shared/services';
import {
  propertyTypeEnum,
  sortOrderEnum,
  DirtyComponent
} from '@shared/models';
import {
  ERROR_GETTING_FILE_TYPES,
  ERROR_GETTING_LINKED_DOCUMENTS,
  ERROR_INVALID_FILE_TYPE,
  ERROR_INVALID_FILE_SIZE,
  ERROR_UPLOADING_DOCUMENT,
  SUCCESS_DOCUMENT_UPLOADED,
  INFO_FILE_SIZE_TYPE,
  ERROR_FILE_SIZE,
  ERROR_FILE_TYPE,
  ERROR_UPLOADING_DOCUMENT_EDIT,
  ERROR_GETTING_FILE_SIZE,
  ERROR_GETTING_CLOUD_CONFIGURATION
} from '@exclusionmodule/constants';
import { ConfirmationService } from 'primeng/api';
import { Table } from 'primeng/table';
import { SplitButton } from 'primeng/splitbutton';
const BYTES_PER_MB = 1048576;
@Component({
  selector: 'app-student-exclusion-linked-document-list',
  templateUrl: './student-exclusion-linked-document-list.component.html',
  styleUrls: ['./student-exclusion-linked-document-list.component.scss'],
  providers: [DialogService],
  standalone: false
})
export class StudentExclusionLinkedDocumentListComponent
  implements OnInit, DirtyComponent, AfterContentChecked
{
  private readonly apiService = inject(ApiServiceService);
  private readonly notificationService = inject(NotificationService);
  private readonly dataSortService = inject(DataSortService);
  private readonly downloadService = inject(DownloadService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly dataLossService = inject(DataLossService);
  private readonly accessibilityService = inject(AccessibilityService);
  private readonly elementRef = inject(ElementRef);

  @ViewChild('addDocumentButton') addDocumentButton: ElementRef;
  @ViewChild('file') fileControl: ElementRef<HTMLElement>;
  @ViewChildren('descriptionInput', { read: ElementRef })
  descriptionInput: QueryList<ElementRef>;
  @ViewChild('allDocumentsList') allDocumentsList: Table;
  @Input() exclusionId: number;
  @Input() isCreateExclusion: boolean;
  @Input() pupilData: pupilModel;
  @Input() isOffRollEditingEnabled: boolean;
  @Output() backToExclusionsList: EventEmitter<any> = new EventEmitter();
  @ViewChildren('editDocumentSplitButton', { read: ElementRef })
  editDocumentSplitButton: QueryList<ElementRef>;
  @ViewChild('editDocumentSplitButton') editDocumentButton: SplitButton;
  @ViewChild('divSupportingDocumentMessage')
  divSupportingDocumentMessage: ElementRef;

  linkedDocuments: exclusionLinkedDocument[];
  linkedDocumentsFoundMessage = false;
  fileToUpload: any[];
  fileData: any;
  fileName: string;
  description: string;
  allowedFileTypes: string;
  allowedFileInfo: string = INFO_FILE_SIZE_TYPE;
  allowedFileSize: number;
  allowedFileSizeInMB: number;
  splitActionButtons = [];
  isUpdateMode = false;
  isEditMode = false;
  fileIndex = -1;
  originalData: exclusionLinkedDocument = null;
  confirmDialogAcceptButtonLabel: string;
  fileId: number;
  isDisableSplitButton = false;
  pupilName: string;
  isDescriptionChanged = false;
  uploadedCount: number;
  isAddDocHidden = false;
  tableRowIndex: number;
  readonly ENTER_KEY: number = 13;
  selectedRowDocument: exclusionLinkedDocument = null;
  selectedRowIndex = -1;
  isUpdateFileClicked = false;
  isDeleteAction = false;
  isAddButtonClicked = false;
  isCancelAction = false;
  isPageLoadedFirstTime = false;
  isSaveClicked = false;
  public supportingDocumentMessage = '';
  public isShowMore = true;
  public anchorTagLinks: string[] = [];
  componentDetail = {
    componentName: 'LinkedDocument',
    isFieldDirty: false
  };

  ngOnInit() {
    this.isPageLoadedFirstTime = true;
    this.bindSupportingDocuments();
  }

  ngAfterContentChecked(): void {
    this.splitButtonSetTitle();
    this.setAttributesForAlertBox();
    this.removeAriaExpandedAttributeForSplitButton();
  }

  // Remove aria expanded attributes to fix axe tool issues for split button
  private removeAriaExpandedAttributeForSplitButton() {
    if (this.allDocumentsList) {
      this.accessibilityService.removeAriaExpandedAttributeForSplitButtons(
        this.allDocumentsList
      );
    }
  }

  private bindSupportingDocuments() {
    const apiRoute = this.getApiRoute('AllExclusions');
    this.GetLinkedDocuments(apiRoute);
    this.GetAllowedFileTypes();
    this.GetOneCloudConfiguration();
    this.GetAllowedFileSize();
    this.getStudentFirstName();
  }

  //get First name of Student
  getStudentFirstName() {
    if (this.pupilData.studentName != null) {
      const index = this.pupilData.studentName.indexOf(' ');
      if (index > 0) {
        this.pupilName = this.pupilData.studentName.substr(0, index);
      } else {
        this.pupilName = this.pupilData.studentName;
      }
    } else {
      this.pupilName = '';
    }
  }

  // Get OneCloud configuration for supporting document message
  GetOneCloudConfiguration() {
    const apiRoute = this.getApiRoute('Configuration');
    this.apiService.get<any>(apiRoute, '').subscribe({
      next: (x: any) => {
        this.supportingDocumentMessage = x.record.supportingDocumentMessage;
      },
      error: () => {
        this.notificationService.showError(ERROR_GETTING_CLOUD_CONFIGURATION);
      }
    });
  }

  // Get allowed file size from app configuration
  GetAllowedFileSize() {
    this.apiService.get(ADMIN_ENDPOINT.GetFileSizeConfiguration, '').subscribe({
      next: (x: any) => {
        this.allowedFileSize = x.value;
        this.allowedFileSizeInMB = this.GetSizeInOtherUnit(
          this.allowedFileSize
        );
        this.allowedFileInfo = this.allowedFileInfo.replace(
          '{0}',
          this.allowedFileSizeInMB.toString()
        );
      },
      error: () => {
        this.notificationService.showError(ERROR_GETTING_FILE_SIZE);
      }
    });
  }

  // Get file extensions that are allowed to upload
  GetAllowedFileTypes() {
    const apiRoute = this.getApiRoute('AllowedFileTypes');
    this.apiService.get<any>(apiRoute, '').subscribe({
      next: (x: any) => {
        const fileExts = x.defparam.paramVal;
        this.SetAllowedFileTypes(fileExts);
        this.allowedFileInfo = this.allowedFileInfo.replace(
          '{1}',
          this.allowedFileTypes.replace(/\./gi, ' ')
        );
      },
      error: () => {
        this.notificationService.showError(ERROR_GETTING_FILE_TYPES);
      }
    });
  }

  // Set the allowed file extensions. Change png,pdf to .png,.pdf
  SetAllowedFileTypes(fileExts: string) {
    fileExts = '.' + fileExts;
    fileExts = fileExts.replace(/,/gi, ',.');
    this.allowedFileTypes = fileExts;
  }

  // Get linked documents
  private GetLinkedDocuments(apiRoute: string) {
    this.apiService.getAll<exclusionLinkedDocument[]>(apiRoute, '').subscribe({
      next: (x: any) => {
        this.isSaveClicked = false;
        this.GetLinkedDocumentList(x);
      },
      error: (x: any) => {
        this.isSaveClicked = false;
        if (x.error.statusCode === 404) {
          this.linkedDocumentsFoundMessage = true;
          this.linkedDocuments = null;

          // While all records are deleted then it should went to the add button
          this.setFocusOnAddOrFirstDocumentButton();
        } else {
          this.notificationService.showError(ERROR_GETTING_LINKED_DOCUMENTS);
        }
      }
    });
  }

  // Assign the list of documents received from api to local collection
  private GetLinkedDocumentList(x: any) {
    this.splitActionButtons = [];
    this.linkedDocuments = x.linkeddocument;

    this.dataSortService.sortData(
      this.linkedDocuments,
      'lastUpdatedDate',
      propertyTypeEnum.date,
      sortOrderEnum.descending
    );

    this.linkedDocuments.forEach((x) => {
      x.fileName = this.getFileName(x.fileName);
      x.documentUploaded = this.isDocumentUploaded(x.uploadedDate);

      const splitActionDeleteButton = [
        {
          label: 'Update File',
          styleClass: 'p-splitbutton p-component align-center',
          command: () => {
            this.updateDocument(x, this.tableRowIndex);
          }
        },
        {
          label: 'Delete',
          styleClass: 'p-splitbutton p-component align-center',
          command: () => {
            this.deleteDocument(x.fileId);
          }
        }
      ];

      this.splitActionButtons.push(splitActionDeleteButton);
    });

    this.setFocusAddEditButton();
  }

  private setFocusAddEditButton() {
    if (this.isDeleteAction) {
      this.setFocusOnEditDocumentSplitButton();
    } else {
      if (this.selectedRowIndex === -1) {
        this.setFocusOnAddOrFirstDocumentButton();
      } else {
        this.setFocusOnSelectedTableRow();
      }
    }
  }

  private setFocusOnAddOrFirstDocumentButton() {
    setTimeout(() => {
      // Focus on first Edit Button when navigated to support document page by
      // clicking support document link from exclusion list page
      if (
        this.editDocumentButton &&
        !this.isDeleteAction &&
        ((this.isAddButtonClicked && !this.isCancelAction) ||
          this.isPageLoadedFirstTime)
      ) {
        this.editDocumentButton?.buttonViewChild?.nativeElement?.focus();
        this.isPageLoadedFirstTime = false;
      } else if (this.addDocumentButton) {
        this.addDocumentButton?.nativeElement?.focus();
      }
    }, 0);

    this.isDeleteAction = false;
  }

  private deleteDocument(fileId: number) {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete the support document record?',
      header: 'Delete Support Document',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      reject: () => {
        this.setFocusOnEditDocumentSplitButton();
      },
      accept: () => {
        this.fileId = fileId;
        const apiRoute = this.getApiRoute('DeleteDocument');
        this.apiService.delete<any>(apiRoute, '').subscribe({
          next: (x: any) => {
            if (parseInt(x.statusCode) === 200) {
              this.notificationService.showSuccess(
                'Support document deleted successfully!'
              );

              this.selectedRowIndex = this.tableRowIndex;
              this.isDeleteAction = true;
              this.bindSupportingDocuments();
            }
          },
          error: () => {
            this.notificationService.showError(
              'Something went wrong, please try again.'
            );
          }
        });
      }
    });
  }

  private updateDocument(exclusionDocument, rowIndex) {
    this.isUpdateFileClicked = true;
    this.selectedRowIndex = rowIndex;
    if (this.allDocumentsList) {
      setTimeout(() => {
        const tableRow = this.allDocumentsList.el.nativeElement
          .querySelector('tbody')
          .querySelectorAll('tr')[this.selectedRowIndex];
        if (tableRow) {
          tableRow.querySelector('.p-button-icon-only')?.focus();
        }
      }, 0);
    }
    this.originalData = exclusionDocument;
    const el: HTMLElement = this.fileControl.nativeElement;
    el.click();

    this.fileIndex = this.linkedDocuments.findIndex(
      (obj) => obj.fileId == this.originalData.fileId
    );

    this.isEditMode = false;
    this.isUpdateMode = true;
    this.isAddButtonClicked = false;
  }

  private editDocument(exclusionDocument: exclusionLinkedDocument, rowIndex) {
    this.isUpdateFileClicked = false;
    //set focus on description of current row when clicked on edit
    this.selectedRowIndex = rowIndex;
    if (this.descriptionInput) {
      setTimeout(() => {
        this.descriptionInput
          .find(
            (x) =>
              x.nativeElement.attributes['ri'].nodeValue === String(rowIndex)
          )
          .nativeElement?.focus();
      }, 0);
    }
    this.dataLossService.addComponentToCheckUnsavedChanges(this);
    this.originalData = exclusionDocument;
    this.fileIndex = this.linkedDocuments.findIndex(
      (obj) => obj.fileId == exclusionDocument.fileId
    );

    this.linkedDocuments[this.fileIndex] = {
      fileId: exclusionDocument.fileId,
      fileDescription: exclusionDocument.fileDescription,
      fileName: exclusionDocument.fileName,
      lastUpdatedDate: new Date(),
      uploadedDate: exclusionDocument.lastUpdatedDate,
      documentUploaded: exclusionDocument.documentUploaded,
      fileData: '',
      isNew: true,
      isExclusionPortalUser: exclusionDocument.isExclusionPortalUser
    };

    this.isUpdateMode = true;
    this.isEditMode = true;
    this.isAddButtonClicked = false;
    this.isDisableSplitButton = true;
  }

  // Get route for api
  getApiRoute(routeFor: string) {
    const routes: Map<string, string> = new Map([
      [
        'AllExclusions',
        EXCLUSION_ENDPOINT.GetExclusionLinkedDocuments.replace(
          '{exclusionId}',
          this.exclusionId?.toString()
        )
      ],
      [
        'AllowedFileTypes',
        ADMIN_ENDPOINT.GetDefParam.replace('{paramCd}', 'LNK_D_FLTR')
      ],
      [
        'PostDocument',
        EXCLUSION_ENDPOINT.CreateExclusionLinkedDocument.replace(
          '{exclusionId}',
          this.exclusionId?.toString()
        )
      ],
      [
        'PutDocument',
        EXCLUSION_ENDPOINT.UpdateExclusionLinkedDocument.replace(
          '{exclusionId}',
          this.exclusionId?.toString()
        ).replace(
          '{linkedDocumentId}',
          this.originalData != undefined || this.originalData != null
            ? this.originalData.fileId?.toString()
            : '-1'
        )
      ],
      [
        'DeleteDocument',
        EXCLUSION_ENDPOINT.DeleteExclusionLinkedDocument.replace(
          '{exclusionId}',
          this.exclusionId?.toString()
        ).replace('{linkedDocumentId}', this.fileId?.toString())
      ],
      ['Configuration', ADMIN_ENDPOINT.GetOneCloudConfigurations]
    ]);
    return routes.get(routeFor);
  }

  // return Yes or No whether file is uploaded
  isDocumentUploaded(uploadedDate: Date): string {
    const date = new Date(uploadedDate);
    return date.getFullYear() == 1 &&
      date.getMonth() == 0 &&
      date.getDate() == 1
      ? 'No'
      : 'Yes';
  }

  // Get the name of the file from the file path
  getFileName(name: string) {
    if (name != null) {
      const startIndex = name.lastIndexOf('\\');
      return name.substr(startIndex + 1);
    }
    return '';
  }

  // Open/download the Exclusion Linked Document
  downloadExclusionLinkedDocument(fileId: number, fileName: string) {
    const apiRoute = this.getApiRoute('AllExclusions') + '/' + fileId;

    this.apiService.get<exclusionLinkedDocument>(apiRoute, '').subscribe({
      next: (x: any) => {
        this.downloadService.downloadFile(x.linkeddocument.fileData, fileName);
      },
      error: (x: any) => {
        if (x.error.statusCode === 404) {
          this.linkedDocumentsFoundMessage = true;
        } else {
          this.notificationService.showError(ERROR_GETTING_LINKED_DOCUMENTS);
        }
      }
    });
    //This is used to retain on same page
    return false;
  }

  // Save the uploaded file
  SaveFile(fileIndex: number) {
    this.isSaveClicked = true;
    this.isCancelAction = false;
    const currentFile = this.linkedDocuments.filter(
      (x) => x.fileId === (this.isUpdateMode ? this.originalData.fileId : -1)
    );
    const filesToBeUploaded = this.linkedDocuments.filter(
      (x) => x.fileId === (this.isUpdateMode ? this.originalData.fileId : -1)
    ).length;
    this.description = currentFile[fileIndex].fileDescription;
    this.fileName = currentFile[fileIndex].fileName;
    const apiRoute = this.getApiRoute(
      this.isUpdateMode ? 'PutDocument' : 'PostDocument'
    );

    if (!this.isUpdateMode) {
      this.apiService
        .post(apiRoute, {
          fileName: this.fileName,
          fileDescription: this.description,
          fileData: this.fileData
        })
        .subscribe({
          next: () => {
            this.dataLossService.removeComponentFromUnsavedChanges(this);
            this.uploadedCount += 1;
            if (this.uploadedCount == filesToBeUploaded) {
              this.rebindSupportDocuments();
            }
          },
          error: (e: any) => {
            this.isSaveClicked = false;
            this.dataLossService.removeComponentFromUnsavedChanges(this);
            this.showFileUploadErrorMessage(e, fileIndex);
          }
        });
    } else {
      this.apiService
        .put(apiRoute, {
          fileName: this.isEditMode
            ? this.originalData.fileName
            : this.fileName,
          fileDescription: this.description,
          fileData: this.isEditMode ? null : this.fileData
        })
        .subscribe({
          next: () => {
            this.dataLossService.removeComponentFromUnsavedChanges(this);
            this.rebindSupportDocuments();
          },
          error: (e: any) => {
            this.isSaveClicked = false;
            this.dataLossService.removeComponentFromUnsavedChanges(this);
            this.showFileUploadErrorMessage(e, fileIndex);
          }
        });

      this.isEditMode = false;
      this.isUpdateMode = false;
    }

    this.isDisableSplitButton = false;
  }

  private showFileUploadErrorMessage(errorMessage, fileIndex) {
    if (this.isEditMode) {
      this.notificationService.showError(ERROR_UPLOADING_DOCUMENT_EDIT);
    } else {
      if (
        errorMessage.error.code === '1.1.500.01' ||
        errorMessage.error.code === '1.1.500.02'
      ) {
        this.resetLinkedDocumentsTableWhenError(fileIndex);
        this.notificationService.showError(
          'File upload is not currently available. Contact MRI Education for assistance'
        );
      } else if (errorMessage.error.code === '1.1.400.01') {
        this.resetLinkedDocumentsTableWhenError(fileIndex);
        this.notificationService.showError(
          'Virus found in the selected file. File not uploaded.'
        );
      } else if (errorMessage.error.code === '1.1.400.02') {
        this.resetLinkedDocumentsTableWhenError(fileIndex);
        this.notificationService.showError('Invalid file type');
      } else if (errorMessage.error.code === '1.1.400.03') {
        this.resetLinkedDocumentsTableWhenError(fileIndex);
        this.notificationService.showError('Error getting filetypes');
      } else if (errorMessage.error.code === '1.1.400.04') {
        this.resetLinkedDocumentsTableWhenError(fileIndex);
        this.notificationService.showError(
          'File size exceeds maximum allowed size'
        );
      } else {
        this.notificationService.showError(ERROR_UPLOADING_DOCUMENT);
      }
    }
  }

  private rebindSupportDocuments() {
    const apiRoute = this.getApiRoute('AllExclusions');
    this.GetLinkedDocuments(apiRoute);
    this.notificationService.showSuccess(SUCCESS_DOCUMENT_UPLOADED);
    if (this.isCreateExclusion) {
      this.backToExclusionsList.emit();
    }
  }

  // When upload cancelled
  uploadCancelled(rowindex: number) {
    this.isCancelAction = true;
    let result = this.isUpdateMode;
    result = result ? this.isDescriptionChanged : true;
    result = result ? this.dataLossService.checkForUnsavedChanges(this) : true;

    if (result) {
      if (
        this.linkedDocuments != null &&
        this.linkedDocuments.length > 0 &&
        !this.isUpdateMode
      ) {
        const lastDocument = this.linkedDocuments.filter((x) => x.fileId == -1)[
          rowindex
        ];
        const newDocumentIndex = this.linkedDocuments.indexOf(lastDocument);
        this.linkedDocuments.splice(newDocumentIndex, 1);
        if (this.fileToUpload && this.fileToUpload.length > 0) {
          const fileTouploadIndex = this.fileToUpload.findIndex(
            (x) => x.name == lastDocument.fileName
          );
          this.fileToUpload.splice(fileTouploadIndex, 1);
        }
        //On create mode, if linkedDocuments length does not contain any document then only set isFieldDirty is true
        if (this.linkedDocuments.length < 1) {
          this.componentDetail.isFieldDirty = false;
        }
      } else if (this.isUpdateMode) {
        this.resetToOriginalData();
      }

      if (this.linkedDocuments.length === 0) {
        this.linkedDocumentsFoundMessage = true;
      }

      this.isDisableSplitButton = false;
      if (!this.isCreateExclusion) {
        this.isAddDocHidden = false;
      }
    }

    //  On cancel bind discernible text for buttons
    this.splitButtonSetTitle();
    this.setFocusAddEditButton();
  }

  onChangeData() {
    this.isDescriptionChanged = true;
    this.componentDetail.isFieldDirty = true;
  }

  private resetToOriginalData() {
    this.linkedDocuments[this.fileIndex] = {
      fileId: this.originalData.fileId,
      fileDescription: this.originalData.fileDescription,
      fileName: this.originalData.fileName,
      lastUpdatedDate: this.originalData.lastUpdatedDate,
      uploadedDate: this.originalData.uploadedDate,
      documentUploaded: this.originalData.documentUploaded,
      fileData: '',
      isNew: false,
      isExclusionPortalUser: this.originalData.isExclusionPortalUser
    };

    this.originalData = null;
    this.isUpdateMode = false;
    this.isDescriptionChanged = false;
    this.componentDetail.isFieldDirty = false;
    this.fileToUpload = [];
  }

  // Upload button click is triggered
  handleFileInput(files: FileList) {
    this.isAddButtonClicked = !this.isUpdateMode && !this.isEditMode;
    if (files.length > 0) {
      if (!this.fileToUpload) {
        this.fileToUpload = [];
      }
      this.dataLossService.addComponentToCheckUnsavedChanges(this);
      this.isDescriptionChanged = true;
      this.componentDetail.isFieldDirty = true;

      const ext = files
        .item(0)
        .name.substring(files.item(0).name.lastIndexOf('.'))
        .toLowerCase();
      if (this.allowedFileTypes.toLowerCase().indexOf(ext) == -1) {
        this.dataLossService.removeComponentFromUnsavedChanges(this);
        this.notificationService.showError(
          ERROR_FILE_TYPE.replace(
            '{0}',
            this.allowedFileTypes.replace(/\./gi, ' ')
          ),
          ERROR_INVALID_FILE_TYPE
        );
        return;
      }
      if (files.item(0).size > this.allowedFileSize) {
        this.dataLossService.removeComponentFromUnsavedChanges(this);
        this.notificationService.showError(
          ERROR_FILE_SIZE.replace(
            '{0}',
            this.GetSizeInOtherUnit(this.allowedFileSize).toString()
          ),
          ERROR_INVALID_FILE_SIZE
        );
        return;
      }
      this.fileToUpload.unshift(files.item(0));
      this.fileName = this.fileToUpload[0].name;
      if (this.linkedDocuments == null) {
        this.linkedDocuments = [];
      }
      this.linkedDocumentsFoundMessage = false;

      const uploadedFile = this.getLinkedDocumentData();
      if (!this.isUpdateMode) {
        // Add new document at the top
        this.selectedRowIndex = -1;
        this.linkedDocuments.unshift(uploadedFile);
      } else {
        // Change to edit mode existing document
        this.linkedDocuments[this.fileIndex] = uploadedFile;
      }

      this.isDisableSplitButton = true;
      if (!this.isCreateExclusion) {
        this.isAddDocHidden = true;
      }
    }
    //set focus on description of current row when clicked on Add Document/Update File
    if (this.descriptionInput) {
      setTimeout(() => {
        this.descriptionInput.last.nativeElement.focus();
      }, 0);
    }
  }

  private getLinkedDocumentData() {
    return {
      fileId: this.isUpdateMode ? this.originalData.fileId : -1,
      fileDescription: this.isUpdateMode
        ? this.originalData.fileDescription
        : '',
      fileName: this.fileToUpload[0].name,
      lastUpdatedDate: this.isUpdateMode
        ? this.originalData.lastUpdatedDate
        : new Date(),
      uploadedDate: this.isUpdateMode
        ? this.originalData.uploadedDate
        : new Date(),
      documentUploaded: 'No',
      fileData: '',
      isNew: true,
      isExclusionPortalUser: this.isUpdateMode
        ? this.originalData.isExclusionPortalUser
        : true
    };
  }

  // Called when upload button is clicked
  postFile(): void {
    this.uploadedCount = 0;
    if (this.isEditMode) {
      this.SaveFile(0);
    } else {
      const fileIndex = 0;
      this.uploadFiles(fileIndex);
    }
    if (!this.isCreateExclusion) {
      this.isAddDocHidden = false;
    }
  }

  uploadFiles(fileIndex: number) {
    const file = this.fileToUpload[fileIndex];
    const reader = new FileReader();
    reader.onload = () => {
      this.fileData = reader.result;
      // reader data contains blob with metadata. Below code remove that metadata and saves only blob data.
      this.fileData = this.fileData.substring(
        this.fileData.indexOf('base64,') + 7
      );
      this.SaveFile(fileIndex);
      fileIndex = fileIndex + 1;
      if (this.fileToUpload.length > fileIndex) {
        this.uploadFiles(fileIndex);
      } else {
        this.fileToUpload = [];
      }
    };
    reader.readAsDataURL(file);
  }

  // Get file size in MB
  GetSizeInOtherUnit(size: number) {
    return Math.ceil(size / BYTES_PER_MB);
  }

  resetData() {
    //Reset Data method is required in Unsaved changes template
  }

  canDeactivate() {
    return this.dataLossService.isDirty();
  }

  private splitButtonSetTitle() {
    if (this.allDocumentsList) {
      this.accessibilityService.splitButtonSetTitle(
        this.allDocumentsList,
        'Manage Document'
      );
    }
  }

  private setAttributesForAlertBox() {
    if (this.elementRef) {
      this.accessibilityService.setAttributesForAlertBox(this.elementRef);
    }
  }

  //find and assign the rowIndex to variable
  findTableRowIndex(rowIndex) {
    this.tableRowIndex = rowIndex;
  }

  //set focus on splitbutton from where delete popup is called
  setFocusOnEditDocumentSplitButton() {
    let isSplitButtonAvailable: boolean =
      this.tableRowIndex + 1 <= this.linkedDocuments?.length ? true : false;
    // If current row is not available then check if there are any records available
    if (!isSplitButtonAvailable && this.linkedDocuments?.length > 0) {
      isSplitButtonAvailable = true;
      this.tableRowIndex = this.tableRowIndex - 1;
    }

    if (isSplitButtonAvailable) {
      setTimeout(() => {
        this.editDocumentSplitButton
          .find(
            (x) =>
              x.nativeElement?.attributes['ri'].nodeValue ===
              String(this.tableRowIndex)
          )
          ?.nativeElement?.querySelector('.p-button-icon-only')
          ?.focus();
      }, 0);

      this.isDeleteAction = false;
    } else {
      this.setFocusOnAddOrFirstDocumentButton();
    }
  }

  //autoclose splitbutton when navigated outside the splitbutton through tab press
  autoCloseSplitButton(event: any) {
    const pressedMenuList = event.target.closest(
      '.p-tieredmenu-overlay >ul >li'
    );
    if (pressedMenuList) {
      const allMenuList = document.querySelectorAll(
        '.p-tieredmenu-overlay >ul >li'
      );
      allMenuList.forEach(() => {
        if (pressedMenuList.innerText === 'Delete') {
          pressedMenuList.closest('.p-tieredmenu')?.remove();
        }
      });
    }
  }

  // Set focus on table row button
  private setFocusOnSelectedTableRow() {
    if (this.allDocumentsList) {
      setTimeout(() => {
        const tableRow = this.allDocumentsList.el.nativeElement
          .querySelector('tbody')
          .querySelectorAll('tr')[this.selectedRowIndex];
        if (tableRow) {
          if (this.isUpdateFileClicked) {
            tableRow.querySelector('.p-button-icon-only')?.focus();
          } else {
            tableRow.querySelector('button')?.focus();
          }
        }
      }, 0);
    }

    this.isDeleteAction = false;
  }

  public onRowSelect(event) {
    if (event.originalEvent.keyCode === this.ENTER_KEY) {
      this.selectedRowIndex = event.originalEvent.target.rowId;
      this.editDocument(this.selectedRowDocument, this.selectedRowIndex);
    }
  }

  // remove the linked document added in table when api returns error (filesize, virus scan, filetype)
  private resetLinkedDocumentsTableWhenError(fileIndex) {
    if (this.isCreateExclusion) {
      this.backToExclusionsList.emit();
    } else if (!this.isUpdateFileClicked) {
      this.linkedDocuments.splice(fileIndex, 1);
      this.setFocusAddEditButton();
    } else {
      this.resetToOriginalData();
      this.setFocusAddEditButton();
    }
  }

  // Handle show more/ show less document message functionality
  public onShowLessLinkClick() {
    this.isShowMore = !this.isShowMore;
    // Get all the anchor tags inside document message
    const allAnchorTags =
      this.divSupportingDocumentMessage.nativeElement.querySelectorAll('a');
    allAnchorTags?.forEach((anchorTag, index) => {
      if (!this.isShowMore) {
        // Only insert href tag value for the first time, wen user clicks on 'ShowLess'
        if (this.anchorTagLinks.length < allAnchorTags.length) {
          this.anchorTagLinks.push(anchorTag?.attributes['href']?.value);
        }

        // Remove href attribute from here as tab should not work while isShowMore is false
        anchorTag.removeAttribute('href');
      } else {
        // In case of isShowMore true, set all href elements with it's appropriate values to appropriate anchor tags
        anchorTag.setAttribute('href', this.anchorTagLinks[index]);
      }
    });
  }
}
